/**
 * t570u: personal widget background colours (Home widgets and Dashboard tiles). Saved per person on the server, so a
 * colour follows them to every device; it only changes their own view. Picking a swatch applies (and saves) at once.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Palette } from 'lucide-react';
import { tintFor, WIDGET_TINTS, withColour, type ColourableKey, type WidgetColour, type WidgetColours, type WidgetTint } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Menu } from '@/components/ui';
import { BottomSheet } from '@/mobile/BottomSheet';

export const coloursKey = (ws: string) => ['home', ws, 'colours'];

export function useWidgetColours() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: coloursKey(ws ?? ''), queryFn: () => api.home.colours(ws!), enabled: Boolean(ws), staleTime: 60_000, select: d => d.colours });
  const colours: WidgetColours = q.data ?? {};
  const set = async (key: ColourableKey, colour: WidgetColour | null) => {
    if (!ws) return;
    const k = coloursKey(ws);
    const before = queryClient.getQueryData<{ colours: WidgetColours }>(k);
    queryClient.setQueryData(k, { colours: withColour(before?.colours ?? {}, key, colour) });
    try { queryClient.setQueryData(k, await api.home.setColour(ws, key, colour)); }
    catch (e) { queryClient.setQueryData(k, before); toast(errorMessage(e), 'error'); }
  };
  return { colours, set, tint: (key: string) => tintFor(colours, key) };
}

/** The CSS variables a tinted widget reads (index.css `[data-tint]`). */
export function tintStyle(t: WidgetTint | null): CSSProperties | undefined {
  if (!t) return undefined;
  return { '--tint-bg': t.bg, '--tint-line': t.line, '--tint-bg-dark': t.darkBg, '--tint-line-dark': t.darkLine } as CSSProperties;
}

/** Wraps a widget so its surfaces take the person's colour; Default renders as before. */
export function Tinted({ k, className, children }: { k: ColourableKey; className?: string; children: ReactNode }) {
  const { tint } = useWidgetColours();
  const t = tint(k);
  return <div className={className} data-tint={t?.id} style={tintStyle(t)} data-testid={`tint-${k}`}>{children}</div>;
}

function Swatches({ value, onPick, title }: { value: WidgetColour | null; onPick: (c: WidgetColour | null) => void; title: string }) {
  const opts: { id: WidgetColour | null; label: string; bg: string; dark: string }[] = [
    { id: null, label: 'Default', bg: '#ffffff', dark: '#0f172a' },
    ...WIDGET_TINTS.map(t => ({ id: t.id, label: t.label, bg: t.bg, dark: t.darkBg })),
  ];
  return (
    <div role="radiogroup" aria-label={`Background for ${title}`} className="grid grid-cols-3 gap-1 p-2" data-testid="colour-swatches">
      {opts.map(o => {
        const on = o.id === value;
        return (
          <button key={o.label} type="button" role="radio" aria-checked={on} onClick={() => onPick(o.id)} data-testid={`swatch-${o.id ?? 'default'}`}
            className="flex flex-col items-center gap-1 rounded-md px-1 py-1.5 text-xs text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700">
            <span className={clsx('relative flex h-7 w-7 items-center justify-center rounded-full border', on ? 'border-ink ring-2 ring-ink ring-offset-1 dark:border-white dark:ring-white dark:ring-offset-slate-800' : 'border-slate-300 dark:border-slate-600')}>
              <span className="absolute inset-0 rounded-full dark:hidden" style={{ background: o.bg }} />
              <span className="absolute inset-0 hidden rounded-full dark:block" style={{ background: o.dark }} />
              {on && <Check className="relative h-3.5 w-3.5 text-ink dark:text-white" aria-hidden />}
            </span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The Background choice for one widget: a small menu of swatches (a bottom sheet on phones). Instant: picking one
 * applies and saves it, then closes.
 */
export function BackgroundPicker({ k, title, sheet, className }: { k: ColourableKey; title: string; sheet?: boolean; className?: string }) {
  const { colours, set } = useWidgetColours();
  const [open, setOpen] = useState(false);
  const value = colours[k] ?? null;
  const pick = (c: WidgetColour | null) => { setOpen(false); void set(k, c); };
  const trigger = (
    <button type="button" aria-label={`Background for ${title}`} title="Background" aria-haspopup="true" aria-expanded={open} data-testid={`bg-${k}`}
      onClick={sheet ? () => setOpen(true) : undefined}
      className={clsx('rounded p-1 text-slate-500 hover:bg-white hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100', className)}>
      <Palette className="h-3.5 w-3.5" />
    </button>
  );
  if (sheet) {
    return (
      <>
        {trigger}
        <BottomSheet open={open} onClose={() => setOpen(false)} title="Background" testId="colour-sheet">
          <Swatches value={value} onPick={pick} title={title} />
        </BottomSheet>
      </>
    );
  }
  return (
    <Menu open={open} setOpen={setOpen} fixed trigger={trigger}>
      <div className="w-60" onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}>
        <p className="px-3 pb-0.5 pt-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400">Background</p>
        <Swatches value={value} onPick={pick} title={title} />
      </div>
    </Menu>
  );
}
